// Inbox-scope context — drives the per-inbox URL pattern (/inbox/:slug/…).
//
// Each inbox-scoped route lives under /inbox/<slug> where <slug> is the
// inbox's `label_namespace` (e.g. "support", "tristan"). InboxScopeProvider
// reads :slug from the URL, finds the matching gmail_inboxes row via the
// cached useGmailInboxes() hook, and exposes it to descendants via
// useCurrentInbox(). Routes that should always be scoped (dashboard,
// my-day, activity, contacts) read currentInbox and apply an inbox_id
// filter to their queries.
//
// Legacy unscoped URLs (/my-day, /dashboard, …) redirect to the user's
// last-visited inbox (persisted in localStorage) → primary inbox →
// first-accessible inbox. The redirect logic is in DefaultInboxRedirect
// below.

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { type GmailInbox } from '@/lib/queries';
import { useViewableInboxes } from '@/lib/use-access';
import { Skeleton } from '@/components/ui/skeleton';

interface InboxScopeValue {
  currentInbox: GmailInbox | null;
  /** All inboxes the signed-in user can access (cached). Used by the switcher. */
  accessibleInboxes: GmailInbox[];
}

const InboxScopeContext = createContext<InboxScopeValue | null>(null);

const LAST_VISITED_KEY = 'pa-last-visited-inbox';

/** Records the slug as "last visited" so unscoped redirects land here next time. */
function rememberLastVisited(slug: string) {
  try {
    window.localStorage.setItem(LAST_VISITED_KEY, slug);
  } catch {
    // localStorage might be disabled — degrades to first-accessible default
  }
}

export function readLastVisited(): string | null {
  try {
    return window.localStorage.getItem(LAST_VISITED_KEY);
  } catch {
    return null;
  }
}

/** Called by unscoped routes (ticket detail) that want to "set" the
 *  user's current inbox context based on the row they're viewing. So
 *  navigating to /tickets/:id from outside any inbox still keeps the
 *  switcher labelled correctly + the next "back to my-day" lands in
 *  the right inbox. */
export function setLastVisitedSlug(slug: string | null | undefined) {
  if (!slug) return;
  try {
    window.localStorage.setItem(LAST_VISITED_KEY, slug);
    // Notify same-tab listeners (localStorage 'storage' events only fire
    // across tabs, not within the same tab). InboxSwitcher listens for this
    // to re-render with the updated slug when unscoped routes (ticket detail)
    // stamp the inbox context.
    window.dispatchEvent(new Event('pa:inbox-slug-changed'));
  } catch {
    // localStorage disabled — no-op
  }
}

/** Wraps an inbox-scoped route subtree. Renders nothing visible itself —
 *  just resolves the slug + provides context. On mismatch (unknown slug
 *  or inbox the user can't access) it redirects to the default. */
export function InboxScopeProvider({ children }: { children: ReactNode }) {
  const { slug } = useParams<{ slug: string }>();
  // Inboxes this person can open (tickets.view), not just rows RLS shows them.
  const inboxesQ = useViewableInboxes();
  const inboxes = inboxesQ.data ?? [];

  const currentInbox = useMemo(() => {
    if (!slug) return null;
    return inboxes.find((i) => i.label_namespace === slug && i.active) ?? null;
  }, [slug, inboxes]);

  useEffect(() => {
    if (currentInbox?.label_namespace) {
      rememberLastVisited(currentInbox.label_namespace);
    }
  }, [currentInbox?.label_namespace]);

  // Still loading the inbox list — render a thin skeleton; don't redirect
  // yet (would create a flash to the default on every page-load).
  if (inboxesQ.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Slug doesn't match any accessible inbox → bounce to default.
  if (!currentInbox) {
    return <Navigate to={defaultInboxPath(inboxes)} replace />;
  }

  const value: InboxScopeValue = { currentInbox, accessibleInboxes: inboxes };
  return <InboxScopeContext.Provider value={value}>{children}</InboxScopeContext.Provider>;
}

/** Returns the current inbox from URL scope, or null if called outside a
 *  scoped route. Most callers use the throw-if-null variant below; this
 *  is exported so global UI (e.g. TopNav switcher) can render safely. */
export function useCurrentInboxOrNull(): InboxScopeValue | null {
  return useContext(InboxScopeContext);
}

/** Inside an inbox-scoped route — guaranteed non-null. Throws if used
 *  outside (a coding error). */
export function useCurrentInbox(): { currentInbox: GmailInbox; accessibleInboxes: GmailInbox[] } {
  const v = useContext(InboxScopeContext);
  if (!v || !v.currentInbox) {
    throw new Error('useCurrentInbox() called outside an /inbox/:slug route');
  }
  return { currentInbox: v.currentInbox, accessibleInboxes: v.accessibleInboxes };
}

/** Build the redirect target for an unscoped landing. Order: last-visited
 *  slug (if still accessible) → primary inbox → first accessible → "/"
 *  (no inboxes — shouldn't happen). The path lands the user on /my-day
 *  inside their chosen inbox. */
export function defaultInboxPath(inboxes: GmailInbox[]): string {
  const accessible = inboxes.filter((i) => i.active);
  if (accessible.length === 0) return '/inboxes';

  const lastVisited = readLastVisited();
  const stuck = lastVisited && accessible.find((i) => i.label_namespace === lastVisited);
  if (stuck) return `/inbox/${stuck.label_namespace}/my-day`;

  const primary = accessible.find((i) => i.is_primary);
  if (primary) return `/inbox/${primary.label_namespace}/my-day`;

  return `/inbox/${accessible[0].label_namespace}/my-day`;
}

/** Drop-in redirect element used for legacy / unscoped routes. Loads the
 *  inbox list once, then redirects to the resolved default. Renders a
 *  thin skeleton while loading. */
export function DefaultInboxRedirect({ subPath = 'my-day' }: { subPath?: string }) {
  const inboxesQ = useViewableInboxes();
  const { search } = useLocation();
  if (inboxesQ.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  const inboxes = inboxesQ.data ?? [];
  const base = defaultInboxPath(inboxes);
  // Swap the trailing '/my-day' for whatever sub-path the caller asked
  // for, so /dashboard redirects to /inbox/<default>/dashboard.
  // Preserve query params so /contacts/view?email=... keeps ?email= after redirect.
  const target = base.replace(/\/my-day$/, '/' + subPath) + search;
  return <Navigate to={target} replace />;
}

/** Build a URL inside the current inbox scope. Used by InboxSwitcher when
 *  preserving the user's current page on switch. */
export function buildInboxPath(slug: string, subPath: string): string {
  const clean = subPath.replace(/^\//, '');
  return `/inbox/${slug}/${clean}`;
}

/** Extracts the inbox slug + sub-view from a pathname. Used by the TopNav
 *  switcher to detect which inbox the user is currently on (the switcher
 *  itself is mounted above InboxScopeProvider so it can't useContext). */
export function parseInboxPath(pathname: string): { slug: string | null; subPath: string } {
  const match = pathname.match(/^\/inbox\/([^/]+)(\/.*)?$/);
  if (!match) return { slug: null, subPath: 'my-day' };
  const slug = match[1];
  const sub = match[2] ? match[2].replace(/^\//, '') : 'my-day';
  return { slug, subPath: sub || 'my-day' };
}

/** Routes that exist inside /inbox/:slug. When switching inboxes, the
 *  switcher tries to preserve the current sub-view if it's in this list;
 *  otherwise it lands the user on my-day. Mirror App.tsx's
 *  inbox-scoped route table. */
export const INBOX_SCOPED_SUB_PATHS = ['my-day', 'dashboard', 'activity', 'contacts', 'contacts/view'] as const;

export function isInboxScopedSubPath(sub: string): boolean {
  const head = sub.split('?')[0];
  return INBOX_SCOPED_SUB_PATHS.some((p) => head === p || head.startsWith(p + '/'));
}
